import { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../../../components/custom-table/custom-table";
import DocumentScreenshotViewerModal from "../../../../../components/modals/documentScreenshotViewerModal/documentScreenshotViewerModal";
import ImageViewerWithBadgesModal from "../../../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import ViewReviewModal from "../../../../../components/modals/ViewReviewModal/ViewReviewModal";
import { DOCUMENT_SCREENSHOT_VIEWER_TITLE } from "../../../../../constants/stringLiterals";
import {
  FilterSection,
  FilterValues,
} from "../../../../../data-model/filterValues";
import { isHealthFacilityFeatureEnabled } from "../../../../../data-model/healthFacility";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { ModalId } from "../../../../../enums/modalId";
import { TableNames } from "../../../../../enums/tableNames";
import { useExportTableData } from "../../../../../hooks/useExportTableData";
import { useReviewIconClick } from "../../../../../hooks/useReviewIconClick";
import { setFilter } from "../../../../../redux/slice/filterValuesSlice";
import { closeModal } from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { adjustTableHeadersForReviewMode } from "../../../../../utils/adjustTableHeadersForReviewMode";
import { handleDocUploadScreenshotViewForOrdersForNewModalDesign } from "../../../../../utils/handleDocUploadScreenshot";
import { handlePropagateEventOnClickInRpaStatusCell } from "../../../../../utils/medicalPaOrdersTableHandlers";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { refreshPostgresFacetCounts } from "../../../../../utils/refreshPostgresFacetCounts";
import { swapUnderscoreHyphen } from "../../../../../utils/swapUnderscoreWithHypen";
import { useFilterOptionsGeneratorFromAlgolia } from "../../../hooks/useFilterOptionsGeneratorFromAlgolia";
import { applySunstateUrologyFilterOverrides } from "../../../medicalPaOrdersAlgolia/utils/applySunstateUrologyFilterOverrides";
import { useModalOpenerForMyPaOrders } from "../../../myPaOrders/hooks/useModalOpenerForMyPaOrders";
import { useMyPaOrdersTableData } from "../../../myPaOrders/hooks/useMyPaOrdersTableData";
import { getFilterCreationConfigForMyPaOrders } from "../../../myPaOrders/utils/filterConfigForMyPaOrders";
import { handleNavigateToNextScreenFromMyPaTable } from "../../../myPaOrders/utils/handleNavigateToNextPageFromMyMedicalPaTable";
import { useFilterAndSearch } from "../../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../../hooks/useInitialDataFetchingForTable";
import { useNextPageDataFetch } from "../../hooks/useNextPageDataFetch";
import { getAllOpenOrdersTableHeaders } from "../../utils/generateTableDataFromAllOpenRecords";

/**
 * Displays the "All Open Orders" worklist table backed by Postgres. Shows every
 * open order (medical_order_status in [order_in_progress, yet_to_start_work_on_order])
 * across all assignees, with an Assigned To column. Handles initial data fetching,
 * filter/search, infinite-scroll pagination, and opens modals for RPA screenshots,
 * document viewers, and review details.
 */
const AllOpenOrdersTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const abortController = useRef<AbortController | null>(null);
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const handleReviewIconClick = useReviewIconClick();
  const orgId = getOrgIdForFetchExternalWorklist();

  // Adjust table headers based on enable_review_mode flag
  const adjustedTableHeaders = useMemo(() => {
    const baseHeaders = getAllOpenOrdersTableHeaders();
    return adjustTableHeadersForReviewMode(
      baseHeaders,
      isHealthFacilityFeatureEnabled(
        singleHealthFacilityDetails,
        "enable_review_mode",
      ),
    );
  }, [singleHealthFacilityDetails]);
  const navigate = useNavigate();
  const {
    myMedicalPaOrdersState: allOpenOrdersState,
    setMyMedicalPaOrdersState: setAllOpenOrdersState,
    tableData,
    setTableData,
  } = useMyPaOrdersTableData();
  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(setAllOpenOrdersState, setTableData);

  const {
    paginationResetCount,
    setSearchTerm,
    searchTerm,
    validFilters,
    filterPayload,
  } = useFilterAndSearch(
    initialDataFetchingExecuted,
    setTableData,
    setAllOpenOrdersState,
  );

  const { exportData, loading: exportingInProgress } = useExportTableData();

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    initialDataFetchingExecuted,
    searchTerm ?? "",
    allOpenOrdersState,
    setAllOpenOrdersState,
    setTableData,
  );

  const { openedModalId, metaData } = useModalOpenerForMyPaOrders();

  // All Open Orders is pinned to open medical_order_status values, so the
  // "Order Status" filter is redundant and intentionally omitted here.
  const allOpenOrdersFilterConfig = useMemo(
    () =>
      getFilterCreationConfigForMyPaOrders(orgId).filter(
        (config) => config.id !== "orderStateStatus",
      ),
    [orgId],
  );

  const { filterData, isPostgresFacetResultsLoading } =
    useFilterOptionsGeneratorFromAlgolia(allOpenOrdersFilterConfig);

  const filterSectionsData = useMemo(
    () => applySunstateUrologyFilterOverrides(filterData, orgId),
    [filterData, orgId],
  );

  /** Re-fetches table data and refreshes sidebar facet counts via Postgres. */
  const handleRefreshButtonClick = () => {
    setInitialDataFetchingExecuted(false);
    refreshPostgresFacetCounts(dispatch);
  };

  return (
    <>
      <div className="h-full">
        <CustomTable
          tableHeaders={adjustedTableHeaders}
          tableData={tableData}
          tableName={TableNames.ALL_OPEN_ORDERS}
          itemsPerPage={20}
          pagesPerView={5}
          count={tableData?.length ?? 0}
          totalCount={allOpenOrdersState?.data?.total_count ?? 0}
          endIndexOfTable={setEndIndex}
          searchingText={(searchText: string) => {
            setSearchTerm(searchText);
          }}
          hideSearchBar={false}
          showInLineLoader={allOpenOrdersState.showinLineLoader}
          shouldLogEvent={true}
          screenName={ScreenNamesForBigQuery.WORKLIST}
          isFetching={allOpenOrdersState.loading}
          requestForPaginationReset={paginationResetCount}
          filterSectionsData={filterSectionsData as FilterSection[]}
          showFilterButton={
            !allOpenOrdersState.loading && !isPostgresFacetResultsLoading
          }
          onFilterApplyClicked={(data: FilterValues) => {
            dispatch(setFilter(data));
          }}
          onRefreshButton={() => {
            handleRefreshButtonClick();
          }}
          showRefreshButton={true}
          isDisabled={
            allOpenOrdersState.loading || allOpenOrdersState.showinLineLoader
          }
          onRowClick={(rowData: any) => {
            handleNavigateToNextScreenFromMyPaTable(
              rowData,
              user?.id ?? "",
              user?.email ?? "",
              swapUnderscoreHyphen("all-open-orders"),
              navigate,
            );
          }}
          onPropagateEventOnClickInRpaStatusCell={(id: string) => {
            handlePropagateEventOnClickInRpaStatusCell(
              id,
              dispatch,
              authStatusOptions,
              abortController,
            );
          }}
          onPropagateEventOnClickInRpaStatusTwoCell={(id: string) => {
            handleDocUploadScreenshotViewForOrdersForNewModalDesign(
              id,
              dispatch,
              abortController,
            );
          }}
          onOverlappingIconClick={(icon_id: string, order_id: string) =>
            handleReviewIconClick(icon_id, order_id, abortController)
          }
          onSideModalClose={handleRefreshButtonClick}
          showExportButton={true}
          onExportButtonClick={() => {
            if (!filterPayload) return;
            exportData(
              { ...filterPayload, export: true },
              "all-open-orders-export.csv",
            );
          }}
          exportingInProgress={exportingInProgress}
          shouldDisableUiSorting={true}
        />
      </div>
      {openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
        <ImageViewerWithBadgesModal
          tabs={metaData?.tabs ?? []}
          showLoading={metaData?.showLoading ?? false}
          title="Write Back Trail"
          onClose={() => {
            abortController.current?.abort();
            abortController.current = null;
          }}
        />
      )}
      {openedModalId === ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL && (
        <DocumentScreenshotViewerModal
          title={DOCUMENT_SCREENSHOT_VIEWER_TITLE}
          onClose={() => {
            abortController.current?.abort();
            abortController.current = null;
          }}
        />
      )}
      {openedModalId === ModalId.VIEW_REVIEW_MODAL && (
        <ViewReviewModal
          onClose={() => {
            dispatch(closeModal());
            abortController.current?.abort();
            abortController.current = null;
          }}
          reviewData={metaData?.reviewData ?? null}
          isLoading={metaData?.isLoading ?? false}
        />
      )}
    </>
  );
};

export default AllOpenOrdersTable;
